import {type Dispatch, type MouseEvent as ReactMouseEvent, type SetStateAction, useEffect} from 'react';
import {alpha, styled} from '@mui/material/styles';
import {SimpleTreeView, TreeItem} from '@mui/x-tree-view';

import {type NavigationTreePlan} from 'client';
import {FormSelectionEnum} from 'interfaces';
import {MinusSquare, PlusSquare} from 'assets/icons';

import {Checkbox, FormControlLabel, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import CircularCheckBox from 'components/InputMenu/Concession/CircularCheckbox';
import ListSubheader from '@mui/material/ListSubheader';
import {type UpdateConcessionForm} from 'hooks/addEdit/useAddEditConcession';
import {RegionSelector} from '../RegionSelector';

export function AssignTree({
  planTreeNavigation,
  concession,
  setConcession,
  selected,
  setSelected,
}: {
  concession: UpdateConcessionForm;
  setConcession: Dispatch<SetStateAction<UpdateConcessionForm>>;
  selected: TreeSelection;
  setSelected: Dispatch<SetStateAction<TreeSelection>>;
  planTreeNavigation: NavigationTreePlan;
}) {
  const {t} = useTranslation('common');

  useEffect(() => {
    setConcession({
      ...concession,
      cageIds: selected.cage,
      siteIds: selected.site,
    });
  }, [selected]);

  const onClick = (checked: boolean) => {
    setConcession({...concession, assignToAllCages: checked});
    setSelected(
      treeAssignAllCages({
        id: planTreeNavigation.id,
        selected,
        planTreeNavigation,
        assignToAllCages: checked,
      }),
    );
  };

  const filteredPlanTree =
    planTreeNavigation.sites &&
    planTreeNavigation.sites.filter((site) =>
      concession.regionId !== '' ? concession.regionId === site.regionId : true,
    );

  return (
    <>
      <ListSubheader disableSticky={true}>{t('assignments')}</ListSubheader>

      <Stack direction="column" spacing={2}>
        {/* Company */}

        <RegionSelector
          {...{regionId: concession?.regionId}}
          setRegionId={(regionId: string) => {
            setConcession({...concession, regionId});
          }}
        />
      </Stack>
      <Stack mx={2} direction="row" spacing={2} paddingTop={1}>
        {/* Assign To All Cages */}
        <FormControlLabel
          control={
            <CircularCheckBox
              checked={concession.assignToAllCages ?? false}
              onClick={(checked) => {
                onClick(checked);
              }}
            />
          }
          label={t('all')}
          data-testid={`assign-to-all-cages`}
        />

        {/* Assign Custom */}
        <FormControlLabel
          control={
            <CircularCheckBox
              checked={!concession.assignToAllCages || false}
              onClick={(checked) => {
                onClick(!checked);
              }}
            />
          }
          label={t('custom')}
          data-testid={`assign-to-all-cages`}
        />
      </Stack>

      {!concession.assignToAllCages && (
        <StyledTreeView
          multiSelect
          slots={{
            expandIcon: PlusSquare,
            collapseIcon: MinusSquare,
          }}
        >
          {filteredPlanTree?.length ? (
            filteredPlanTree?.map((site) => {
              const isSiteSelected = selected[FormSelectionEnum.site].includes(site.id);
              const indeterminate = site.cages?.some((cage) => selected[FormSelectionEnum.cage].includes(cage.id));
              return (
                <StyledTreeItem
                  key={site.id}
                  itemId={`${FormSelectionEnum.site}${site.id}`}
                  label={
                    <CheckboxedLabel
                      id={site.id}
                      label={site.name}
                      checked={isSiteSelected}
                      indeterminate={indeterminate}
                      onClick={(event) => {
                        event.stopPropagation();
                        const selection = treeAssignerHandler({
                          id: site.id,
                          siteId: site.id,
                          type: FormSelectionEnum.site,
                          selected,
                          planTreeNavigation,
                        });
                        setSelected(selection);
                      }}
                    />
                  }
                >
                  {site.cages &&
                    site.cages.map((cage) => {
                      const isCageSelected = selected[FormSelectionEnum.cage].includes(cage.id);
                      return (
                        <StyledTreeItem
                          key={cage.id}
                          itemId={`${FormSelectionEnum.cage}${cage.id}`}
                          label={
                            <CheckboxedLabel
                              id={cage.id}
                              label={cage.name}
                              checked={isCageSelected || isSiteSelected}
                              onClick={(event) => {
                                event.stopPropagation();
                                const selection = treeAssignerHandler({
                                  id: cage.id,
                                  siteId: site.id,
                                  type: FormSelectionEnum.cage,
                                  selected,
                                  planTreeNavigation,
                                });
                                setSelected(selection);
                              }}
                            />
                          }
                        />
                      );
                    })}
                </StyledTreeItem>
              );
            })
          ) : (
            <Typography variant="caption" color="error.main">
              {t('no sites assigned to this region')}
            </Typography>
          )}
        </StyledTreeView>
      )}
    </>
  );
}

const CheckboxedLabel = ({
  id,
  label,
  checked,
  indeterminate,
  onClick,
}: {
  id: string;
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onClick: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  return (
    <span>
      <StyledCheckbox
        checked={checked}
        indeterminate={(!checked && indeterminate) ?? false}
        onClick={onClick}
        data-testid={`concessions-assign-tree-element-id-${id}`}
      />
      {label}
    </span>
  );
};

const StyledTreeItem = styled(TreeItem)(({theme}) => ({
  iconContainer: {
    padding: theme.spacing(1),
    '& .close': {
      opacity: 0.3,
    },
  },
  group: {
    marginLeft: 7,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
  label: {
    fontSize: '14px',
    borderRadius: '100px',
    '&:hover': {
      borderRadius: '100px',
    },
  },
}));

const StyledTreeView = styled(SimpleTreeView)(({theme}) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.35),
  width: '100%',
  border: '1px solid',
  borderRadius: theme.spacing(0.5),
  fontSize: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  borderColor: alpha(theme.palette.text.secondary, 0.1),
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.text.secondary,
  },
}));

const StyledCheckbox = styled(Checkbox)(({theme}) => ({
  padding: '2px',
  marginRight: '4px',
  color: `${theme.palette.primary.main} !important`,
  '&$checked': {
    color: theme.palette.primary.main,
  },
}));
export interface TreeSelection {
  [FormSelectionEnum.plan]: string[];
  [FormSelectionEnum.site]: string[];
  [FormSelectionEnum.cage]: string[];
}

export const selectionHandler = (selected: string[], id: string) => {
  if (selected.includes(id)) {
    return selected.filter((selection) => selection !== id);
  } else {
    return selected.concat(id);
  }
};

const treeAssignerHandler = ({
  id,
  siteId,
  type,
  selected,
  planTreeNavigation,
}: {
  id: string;
  siteId?: string;
  type: FormSelectionEnum;
  selected: TreeSelection;
  planTreeNavigation: NavigationTreePlan;
}): TreeSelection => {
  if (type === FormSelectionEnum.site) {
    const siteSelection = selectionHandler(selected[FormSelectionEnum.site], id);
    let cages: string[] = selected.cage;
    if (siteSelection.includes(id)) {
      planTreeNavigation.sites?.map((site) => site.id === id && site.cages?.map((cage) => cages.push(cage.id)));
    } else {
      const cagesToRemove: string[] = [];
      planTreeNavigation.sites?.map((site) => site.id === id && site.cages?.map((cage) => cagesToRemove.push(cage.id)));
      cages = cages.filter((id) => !cagesToRemove.includes(id));
    }
    return {
      [FormSelectionEnum.plan]: [],
      [FormSelectionEnum.site]: siteSelection,
      [FormSelectionEnum.cage]: cages,
    };
  } else if (type === FormSelectionEnum.cage) {
    const selectedSites = selected[FormSelectionEnum.site];
    const selectedCages = selectionHandler(selected.cage, id);
    const siteCagesSelected = planTreeNavigation.sites?.find((site) => site.id === siteId)?.cages;
    const allCagesSelectedNot =
      siteCagesSelected && siteCagesSelected.map((cage) => selectedCages.includes(cage.id)).includes(false);

    const uniqueSelectedSites = () => {
      if (!allCagesSelectedNot && siteId) {
        if (selectedSites.includes(siteId)) {
          return selectedSites;
        } else {
          return selectedSites.concat(siteId);
        }
      } else {
        return selectedSites.filter((id) => siteId !== id);
      }
    };

    return {
      [FormSelectionEnum.plan]: [],
      [FormSelectionEnum.site]: uniqueSelectedSites(),
      [FormSelectionEnum.cage]: selectedCages,
    };
  } else {
    throw new Error(`${type} not haddled`);
  }
};

const treeAssignAllCages = ({
  id,
  selected,
  planTreeNavigation,
  assignToAllCages,
}: {
  id: string;
  selected: TreeSelection;
  planTreeNavigation: NavigationTreePlan;
  assignToAllCages?: boolean;
}) => {
  if (assignToAllCages) {
    const planSelection = selectionHandler(selected[FormSelectionEnum.plan], id);
    const sites: string[] = [];
    const cages: string[] = [];
    if (planSelection.length) {
      planTreeNavigation.sites?.map((site) => sites.push(site.id));
      planTreeNavigation.sites?.map((site) => site.cages?.map((cage) => cages.push(cage.id)));
    }
    return {
      [FormSelectionEnum.plan]: planSelection,
      [FormSelectionEnum.site]: sites,
      [FormSelectionEnum.cage]: cages,
    };
  } else {
    return {
      [FormSelectionEnum.plan]: [],
      [FormSelectionEnum.site]: [],
      [FormSelectionEnum.cage]: [],
    };
  }
};
